import { ChevronDown } from 'lucide-react';
import { useState, type CSSProperties, type FC } from 'react';

import type { StorefrontMenuItem } from '@/features/storefront/api/storefront';
import { StorefrontContainer } from '@/features/storefront/components/StorefrontContainer';
import {
  HeaderBannerRail,
  HeaderCategoryLinks,
  MenuLink,
  MobileNav,
  StoreLogo,
} from '@/features/storefront/layouts/chrome/ChromeParts';
import { menuItems, type HeaderProps } from '@/features/storefront/layouts/chrome/types';
import { cn } from '@/lib/cn';

/** The masthead's navigation voice: small capitals, tightly spaced. */
const KINETIC_NAV = 'items-center gap-6 whitespace-nowrap text-[14px] font-medium uppercase tracking-[0.04em]';

/** A nav link: an underline in the signal colour marks the one under the pointer. */
const NAV_LINK =
  'border-b-2 border-transparent pb-0.5 hover:border-[color:var(--storefront-primary)]';

/**
 * The rail's ground: the primary orange rather than the text colour, by the same
 * variable swap the other families make. The rail sets its copy in the page
 * colour, so the result is near-black type on orange.
 */
const RAIL_STYLE = {
  '--storefront-palette-text': 'var(--storefront-palette-primary)',
} as CSSProperties;

/** Whether an item opens a whole panel of columns rather than a short list. */
function isMega(item: StorefrontMenuItem): boolean {
  return (item.items ?? []).some((child) => (child.items ?? []).length > 0);
}

/**
 * One item on the bar, and whatever the merchant filed under it.
 *
 * An item whose children have children - "Shop" → Training → Yoga - opens a
 * **mega panel** the width of the page, one column per child with its links
 * under a condensed capital heading. An item with a flat list of children
 * opens a small card under itself. Either way the item stays a link. Opens on
 * hover and on focus, closes on Escape and when focus leaves.
 */
function NavItem({ item, preview }: { item: StorefrontMenuItem; preview?: boolean }) {
  const [open, setOpen] = useState(false);
  const children = item.items ?? [];
  const mega = isMega(item);

  if (children.length === 0) {
    return <MenuLink item={item} preview={preview} className={NAV_LINK} />;
  }

  return (
    <div
      className={cn('flex h-full items-center', !mega && 'relative')}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') setOpen(false);
      }}
    >
      <span className="flex items-center gap-1">
        <MenuLink item={item} preview={preview} className={NAV_LINK} />
        <ChevronDown
          className={cn('size-3.5 transition-transform', open && 'rotate-180')}
          aria-hidden
        />
      </span>

      {open && mega ? (
        <div
          className="absolute inset-x-0 top-full z-40 border-t"
          style={{
            backgroundColor: 'var(--storefront-background)',
            borderColor: 'var(--storefront-surface)',
          }}
        >
          <StorefrontContainer className="grid grid-cols-2 gap-x-10 gap-y-8 py-10 md:grid-cols-4">
            {children.map((column) => (
              <div key={`${column.label}:${column.url}`}>
                <MenuLink
                  item={column}
                  preview={preview}
                  className="mb-4 block text-2xl font-bold uppercase leading-none hover:opacity-70"
                />
                <div className="flex flex-col gap-3 text-[15px] normal-case tracking-normal">
                  {(column.items ?? []).map((link) => (
                    <MenuLink
                      key={`${link.label}:${link.url}`}
                      item={link}
                      preview={preview}
                      className="hover:opacity-60"
                    />
                  ))}
                </div>
              </div>
            ))}
          </StorefrontContainer>
        </div>
      ) : null}

      {open && !mega ? (
        <div className="absolute left-1/2 top-full z-40 -translate-x-1/2 pt-2">
          <div
            className="min-w-52 border py-2 normal-case tracking-normal"
            style={{
              backgroundColor: 'var(--storefront-background)',
              borderColor: 'var(--storefront-surface)',
            }}
          >
            {children.map((child) => (
              <MenuLink
                key={`${child.label}:${child.url}`}
                item={child}
                preview={preview}
                className="block px-5 py-2 hover:opacity-60"
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * A sports shop's masthead: the orange rail with the standing promises, then one
 * dark deck - wordmark left in heavy italic capitals, the menu across the
 * middle in small capitals, the shopper's corner right.
 *
 * Like the other families, the bar stands the shop's own shelves in when the
 * merchant has built no menu, and below the large breakpoint the menu moves
 * into the sheet behind the menu button.
 */
export const Header: FC<HeaderProps> = ({
  storeName,
  logoUrl,
  description,
  header,
  menu,
  endSlot,
  preview,
}) => {
  const items = menuItems(menu);

  return (
    <header
      className={cn('relative border-b', header.sticky && !preview && 'sticky top-0 z-40')}
      style={{
        backgroundColor: 'var(--storefront-background)',
        borderColor: 'var(--storefront-surface)',
      }}
    >
      <div style={RAIL_STYLE}>
        <HeaderBannerRail header={header} fallback={description} preview={preview} />
      </div>

      <StorefrontContainer className="flex min-h-[4.5rem] items-stretch gap-3 sm:gap-6">
        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          <MobileNav menu={menu} hiddenFrom="lg:hidden" preview={preview} categoryFallback />
          <StoreLogo
            storeName={storeName}
            logoUrl={logoUrl}
            logoWidthPx={header.logo_width_px}
            showStoreName={header.show_store_name}
            preview={preview}
            className="gap-2.5"
            nameClassName="text-[1.35rem] sm:text-[1.7rem] font-extrabold uppercase italic leading-none tracking-[0.01em]"
            nameStyle={{ fontFamily: 'var(--storefront-heading-font)' }}
            nameSuffix={<span style={{ color: 'var(--storefront-primary)' }}>.</span>}
          />
        </div>

        <div className="hidden min-w-0 flex-1 justify-center lg:flex preview-narrow:hidden!">
          {items.length > 0 ? (
            <nav aria-label={menu?.name} className={cn('flex', KINETIC_NAV)}>
              {items.map((item) => (
                <NavItem key={`${item.label}:${item.url}`} item={item} preview={preview} />
              ))}
            </nav>
          ) : preview ? null : (
            <HeaderCategoryLinks
              className={cn('flex', KINETIC_NAV)}
              linkClassName={NAV_LINK}
              header={header}
              limit={6}
            />
          )}
        </div>

        <div className="ms-auto flex shrink-0 items-center gap-3 sm:gap-5">{endSlot}</div>
      </StorefrontContainer>
    </header>
  );
};
